import { ref, onUnmounted, readonly } from 'vue'
import { MercureClient } from '@/modules/common/services/websocket/MercureClient'
import { EventBus } from '@/modules/common/services/websocket/EventBus'
import type {
  MercureConfig,
  ConnectionStatus,
  ConnectionMetrics,
  EventCallback,
  UnsubscribeFunction,
} from '@/modules/common/services/websocket/types'

// Global instances
let globalMercureClient: MercureClient | null = null
let globalEventBus: EventBus | null = null

// Reactive state must be shared across every useMercure() call site (App shell,
// NotificationCenter, etc.) - globalMercureClient is a singleton, so per-call local
// refs used to go stale/orphaned whenever a second call site (re)created the client:
// its status_change subscription pointed at the new client while the first call
// site's refs kept displaying whatever status they were left at (e.g. "Отключено"
// forever, "Переподключить" reconnecting a client no one was listening to anymore).
const isConnected = ref(false)
const connectionStatus = ref<ConnectionStatus>('disconnected')
const connectionError = ref<string | null>(null)
const metrics = ref<ConnectionMetrics | null>(null)

export function useMercure() {
  const getClient = (): MercureClient | null => {
    return globalMercureClient
  }

  const getEventBus = (): EventBus => {
    if (!globalEventBus) {
      globalEventBus = new EventBus()
    }
    return globalEventBus
  }

  const connect = async (config: MercureConfig): Promise<void> => {
    try {
      if (globalMercureClient) {
        globalMercureClient.disconnect()
      }

      globalMercureClient = new MercureClient(config)

      // Subscribe to status changes
      globalMercureClient.subscribe('status_change', (event) => {
        const payload = (event.data || {}) as { status?: ConnectionStatus }
        connectionStatus.value = payload.status || 'disconnected'
        isConnected.value = payload.status === 'connected'

        if (payload.status === 'error') {
          connectionError.value = 'Connection failed'
        } else {
          connectionError.value = null
        }
      })

      // Forward all events to global event bus
      globalMercureClient.subscribe('*', (event) => {
        getEventBus().emit(event)
      })

      await globalMercureClient.connect()

      // Update metrics periodically
      const metricsInterval = setInterval(() => {
        if (globalMercureClient) {
          metrics.value = globalMercureClient.getMetrics()
        } else {
          clearInterval(metricsInterval)
        }
      }, 5000)
    } catch (error) {
      connectionError.value = error instanceof Error ? error.message : 'Unknown connection error'
      throw error
    }
  }

  const disconnect = (): void => {
    if (globalMercureClient) {
      globalMercureClient.disconnect()
      globalMercureClient = null
    }

    isConnected.value = false
    connectionStatus.value = 'disconnected'
    connectionError.value = null
    metrics.value = null
  }

  const subscribe = (eventType: string, callback: EventCallback): UnsubscribeFunction => {
    return getEventBus().on(eventType, callback)
  }

  const reconnect = async (): Promise<void> => {
    if (globalMercureClient) {
      await globalMercureClient.reconnect()
    }
  }

  const updateToken = (newToken: string): void => {
    if (globalMercureClient) {
      globalMercureClient.updateToken(newToken)
    }
  }

  const addTopic = (topic: string): void => {
    if (globalMercureClient) {
      globalMercureClient.addTopic(topic)
    }
  }

  const removeTopic = (topic: string): void => {
    if (globalMercureClient) {
      globalMercureClient.removeTopic(topic)
    }
  }

  // Cleanup on unmount
  onUnmounted(() => {
    // Don't disconnect global client on component unmount
    // It should persist across components
  })

  return {
    // State
    isConnected: readonly(isConnected),
    connectionStatus: readonly(connectionStatus),
    connectionError: readonly(connectionError),
    metrics: readonly(metrics),

    // Methods
    connect,
    disconnect,
    subscribe,
    reconnect,
    updateToken,
    addTopic,
    removeTopic,
    getClient,
    getEventBus,
  }
}
